// The open project, its undo history, and change notifications. The project object (file
// format v2, @dipfield/project) is the app state; modules read it and edit it in place
// after pushHistory(). Saving to IndexedDB is automatic. GPL-3.0-or-later.
import { newProject, type Project } from "@dipfield/project";
import * as db from "./persist.js";

let P: Project = newProject();
export const project = () => P;

type Ev = "load" | "restore" | "history" | "saved";
const subs = new Map<Ev, Set<() => void>>();
export function on(ev: Ev, f: () => void) {
  if (!subs.has(ev)) subs.set(ev, new Set());
  subs.get(ev)!.add(f);
  return () => { subs.get(ev)!.delete(f); };
}
const emit = (ev: Ev) => subs.get(ev)?.forEach((f) => f());

// ---------- undo / redo: data edits (rows, sets, traverses, slope, tunnel) ----------
const undoStack: string[] = [], redoStack: string[] = [];
const snap = () => JSON.stringify({ data: P.data, sets: P.sets, slope: P.slope, tunnel: P.tunnel });
export function pushHistory() {
  undoStack.push(snap()); if (undoStack.length > 100) undoStack.shift();
  redoStack.length = 0; emit("history");
}
/** Forget the last pushHistory() (an edit that turned out to change nothing). */
export function dropHistory() { undoStack.pop(); emit("history"); }
function restore(s: string) { Object.assign(P, JSON.parse(s)); emit("restore"); emit("history"); touched(); }
export function undo() { const s = undoStack.pop(); if (s) { redoStack.push(snap()); restore(s); } }
export function redo() { const s = redoStack.pop(); if (s) { undoStack.push(snap()); restore(s); } }
export const canUndo = () => undoStack.length > 0;
export const canRedo = () => redoStack.length > 0;

// ---------- open / save ----------
/** Make `p` the open project (new, opened from a file or from the project list). */
export function load(p: Project, o: { save?: boolean } = {}) {
  P = p; undoStack.length = 0; redoStack.length = 0;
  emit("load"); emit("history");
  db.setCurrent(P.meta.id);
  if (o.save !== false) touched(0);
}
let timer = 0, dirty = false;
async function saveNow() {
  clearTimeout(timer); if (!dirty) return; dirty = false;
  P.meta.savedAt = new Date().toISOString();
  if (await db.saveProject(P)) emit("saved");
}
/** Something changed: save soon (debounced), and at the latest when the page is hidden. */
export function touched(ms = 400) {
  dirty = true; clearTimeout(timer);
  timer = window.setTimeout(saveNow, ms);
}
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => void saveNow());
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") void saveNow(); });
}
export function rename(name: string) { const n = name.trim(); if (!n) return; P.meta.name = n.slice(0, 120); touched(0); emit("saved"); }

/** Restore the project open last time, if there is one. */
export async function boot(): Promise<boolean> {
  const id = await db.getCurrent();
  const p = id ? await db.loadProject(id) : null;
  if (p) { load(p, { save: false }); return true; }
  return false;
}
